import { KatButton, KatDropdownButton, KatIcon } from "@amzn/katal-react";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { TestStatus } from "src/api/interfaces/nest";
import type { NestTestResult } from "src/api/interfaces/nest/history";
import { NestStatusBadge } from "../../NestStatusBadge";
import { NestHistoryErrorTable } from "../NestHistoryErrorTable";
import { NestPanel } from "../../NestPanel";
import "./index.css";
import { useNest } from "src/components/Contexts/NestContext";
import { useCountrySelection } from "src/components/Contexts/CountrySelectionContext";
import Markdown from "markdown-to-jsx";
import { downloadAction, getDownloadOptions } from "./downloadHandler";

interface NestHistoryDetailProps {
  test: NestTestResult;
  onBack: () => void;
  onNewTest: (useCaseId: string) => void;
  onClose?: () => void;
  showBack?: boolean;
}

export function NestHistoryDetail({
  test,
  onBack,
  onNewTest,
  onClose,
  showBack = true,
}: NestHistoryDetailProps) {
  const { t } = useTranslation();
  const passed = test.status === TestStatus.PASS;
  const noData = test.status === TestStatus.NO_DATA;
  const { partnerCode } = useNest();
  const { selectedCountry } = useCountrySelection();
  const countryCode = selectedCountry!.countryCode.toLowerCase();
  const [downloadingState, setDownloadingState] = useState(false);

  return (
    <NestPanel
      title={t("aic_nest_history_detail_title", { fileName: test.fileName })}
      onBack={showBack ? onBack : undefined}
      onClose={onClose}
      backAriaLabel={t("aic_nest_detail_back_label")}
      closeAriaLabel={t("aic_nest_detail_close_label")}
      footer={
        <div className="nest-detail-actions">
          <KatButton
            variant="tertiary"
            onClick={() => onNewTest(test.useCaseId)}
            disabled={!!partnerCode}
          >
            <KatIcon name="description" size="small" />
            <span>{t("aic_nest_history_detail_newTest")}</span>
          </KatButton>
          <KatDropdownButton
            id="nest-download-input-file-dropdown"
            size="base"
            disabled={downloadingState || noData}
            variant="secondary"
            options={getDownloadOptions(t)}
            onAction={downloadAction(test.id, setDownloadingState)}
          />
        </div>
      }
    >
      <div className="nest-detail-content">
        <div
          className={`nest-detail-status nest-detail-status--${passed ? "passed" : "failed"}`}
        >
          <NestStatusBadge status={test.status} />
          <p className="nest-detail-message">
            {passed
              ? t("aic_nest_history_detail_passed")
              : t("aic_nest_history_detail_validation_summary", {
                  total: test.totalValidations,
                  passed: test.totalValidations - test.failedValidations,
                })}
          </p>
        </div>
        {test.errors?.length > 0 ? (
          <div className="nest-detail-error-list">
            <h6 className="nest-detail-error-list-title">
              {t("aic_nest_history_detail_errorList")}
            </h6>
            <NestHistoryErrorTable data={test.errors} />
          </div>
        ) : (
          <Markdown>
            {t(`aic_nest_pass_validation_disclaimer_${countryCode}`)}
          </Markdown>
        )}
      </div>
    </NestPanel>
  );
}
